/* eslint-disable react-hooks/exhaustive-deps */
import AddCircleIcon from "@mui/icons-material/AddCircle";
import { Box, Stack, Typography } from "@mui/material";
import { debounce } from "lodash";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { api } from "../../api/api";
import { IUser, IUsersAutoSearch } from "./AutoCompleteSearches.types";
import { useBillingCompanyContext } from "../../providers";
import { EnrAutoComplete } from "../basic/EnrAutoComplete";
import {
  removeDuplicateItems,
  removeDuplicateUsers,
} from "../../helpers/removeDuplicateItems";

const INITIAL_STATE: IUser = {
  user_name: "",
  legal_entity: null,
  full_name: "",
  first_name: "",
  personal_email: "",
  user_uuid: "",
  last_name: "",
  job_title: null,
  manager_uuid: null,
  user_type: null,
  assigned_phone_number: null,
  shared_email: null,
  mobile: null,
  home_phone: null,
  linkedin_profile: null,
  hire_date: null,
  last_day_at_work: null,
  branch: null,
  department: null,
  fax: null,
  date_of_birth: null,
  mother_maiden_name: null,
  photo: null,
  signature: null,
  street_address: null,
  unit_or_suite: null,
  city: null,
  province_or_state: null,
  postal_code: null,
  country: null,
  languages_known: null,
  documents: null,
  status: "ACTIVE",
};

export const UsersAutoSearch: React.FC<IUsersAutoSearch> = ({
  label,
  showTopLabel = true,
  value,
  onSelect,
  disabled,
  error,
  roleGroup,
  roleValue,
  hideAddButton,
  placeholder = "",
  isHighlighted = false,
  staticOptions,
}) => {
  const companyOutlet = useBillingCompanyContext();

  const [options, setOptions] = useState<IUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [hasFocused, setHasFocused] = useState(false);
  const [openUserDialog, setOpenUserDialog] = useState(false);

  const abortRef = useRef<AbortController | null>(null);

  /* ----------------------------- Query Builder ----------------------------- */

  const buildQuery = (searchText: string) => {
    const params = new URLSearchParams();

    if (roleGroup) params.append("role_group", roleGroup);
    if (roleValue) params.append("role_value", roleValue);

    if (searchText) {
      params.append("columns", "first_name,last_name");
      params.append("value", searchText);
    }

    params.append("status", "ACTIVE");
    params.append(
      "company_outlet_uuid",
      companyOutlet.comapny.company_outlet_uuid,
    );

    return params.toString();
  };

  /* ----------------------------- API Fetcher ----------------------------- */

  const fetchUsers = async (searchText: string) => {
    try {
      abortRef.current?.abort();
      abortRef.current = new AbortController();

      setLoading(true);

      const query = buildQuery(searchText);

      const res = await api.get(`/user/get-user?${query}`, {
        signal: abortRef.current.signal,
      });

      let data: IUser[] = res.data.data || [];

      // Inject selected value if missing
      if (value?.user_uuid && value?.user_name) {
        const exists = data.some((u) => u.user_uuid === value.user_uuid);
        if (!exists) {
          const userName = value.user_name.split(" ");
          data = [
            {
              ...INITIAL_STATE,
              first_name: userName[0] || "",
              last_name: userName[1] || "",
            },
            ...data,
          ];
        }
      }
      setOptions(removeDuplicateItems(data, "user_uuid"));
    } catch (e) {
      if ((e as any).name !== "CanceledError") {
        console.error("User search failed", e);
      }
    } finally {
      setLoading(false);
    }
  };

  /* ----------------------------- Debounced Search ----------------------------- */

  const debouncedSearch = useMemo(
    () =>
      debounce((text: string) => {
        fetchUsers(text);
      }, 500),
    [roleGroup, roleValue],
  );

  useEffect(() => {
    fetchUsers("");
    return () => {
      debouncedSearch.cancel();
      abortRef.current?.abort();
    };
  }, [roleGroup, roleValue, companyOutlet.comapny.company_outlet_uuid]);

  /* ----------------------------- Input ----------------------------- */

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearch(val);
    debouncedSearch(val);
  };

  const handleFocus = () => {
    if (!hasFocused) {
      setHasFocused(true);
      fetchUsers("");
    }
  };

  /* ----------------------------- Selected Value Sync ----------------------------- */

  useEffect(() => {
    if (!value) {
      return;
    }

    if (value.user_uuid) {
      // Static sentinel options (e.g. "Unassigned") are not real users — don't
      // try to fetch them from the API; just show their name.
      if (staticUuids.has(value.user_uuid)) {
        setSearch(value.user_name || "");
        return;
      }
      if (!value.user_name) {
        api.get(`/user/get-user?user_uuid=${value.user_uuid}`)
          .then((res) => {
            const data: IUser[] = res.data.data || [];
            if (data.length > 0) {
              const u = data[0];
              const fullName = `${u.first_name} ${u.last_name || ""}`.trim();
              setSearch(fullName);
              setOptions((prev) => removeDuplicateItems([u, ...prev], "user_uuid"));
            }
          })
          .catch((e) => {
            console.error("Failed to fetch user by user_uuid", e);
          });
      } else {
        setSearch(value.user_name || "");
        setOptions((prev) => {
          const exists = prev.some((u) => u.user_uuid === value.user_uuid);
          if (exists) return removeDuplicateItems(prev, "user_uuid");
          const userName = value.user_name.split(" ");
          return removeDuplicateItems(
            [
              {
                ...INITIAL_STATE,
                first_name: userName[0] || "",
                last_name: userName[1] || "",
                user_uuid: value.user_uuid,
              },
              ...prev,
            ],
            "user_uuid",
          );
        });
      }
    } else {
      setSearch("");
    }
  }, [value?.user_uuid, value?.user_name]);

  /* ----------------------------- Helpers ----------------------------- */

  const staticUuids = useMemo(
    () => new Set((staticOptions || []).map((o) => o.user_uuid)),
    [staticOptions],
  );

  // Static sentinel options (e.g. "Unassigned") first, then fetched users.
  const mergedOptions = useMemo(() => {
    if (!staticOptions?.length) return options;
    const withoutStatics = options.filter((o) => !staticUuids.has(o.user_uuid));
    return [...staticOptions, ...withoutStatics];
  }, [options, staticOptions, staticUuids]);

  const selectedValue = useMemo(
    () => mergedOptions.find((o) => o.user_uuid === value?.user_uuid) || null,
    [mergedOptions, value],
  );

  const getOptionLabel = (opt: IUser | string) => {
    if (typeof opt === "string") return opt;
    if (staticUuids.has(opt.user_uuid)) return opt.user_name || "";
    return `${opt.first_name} ${opt.last_name || ""}`;
  };

  /* ----------------------------- UI ----------------------------- */

  return (
    <>
      {label &&
        showTopLabel &&
        !hideAddButton &&
        search &&
        options.length === 0 &&
        !loading && (
          <Box
            display="flex"
            alignItems="center"
            mt={1}
            onClick={() => setOpenUserDialog(true)}
          >
            <AddCircleIcon fontSize="small" color="primary" />
            <Typography ml={0.5} fontWeight={500} color="primary">
              Add User
            </Typography>
          </Box>
        )}

      <Stack>
        <EnrAutoComplete
          fullWidth
          disabled={disabled}
          isHighlighted={isHighlighted}
          // Input (default renderInput handles label/error/loading)
          label={label}
          placeholder={placeholder}
          error={error}
          loading={loading}
          // Static "Unassigned" sentinel row → italic/muted + divider.
          isStaticOption={(option) => staticUuids.has(option.user_uuid)}
          options={mergedOptions}
          value={selectedValue}
          inputValue={search}
          getOptionLabel={getOptionLabel}
          filterOptions={(opts, params) => {
            const input = params.inputValue.trim().toLowerCase();
            // Static options are always shown; fetched options come pre-filtered
            // from the server, so keep them as-is.
            if (!input) return opts;
            return opts.filter((o) => {
              if (staticUuids.has(o.user_uuid)) {
                return getOptionLabel(o).toLowerCase().includes(input);
              }
              return true;
            });
          }}
          isOptionEqualToValue={(a, b) => a.user_uuid === b?.user_uuid}
          onFocus={handleFocus}
          onChange={(_, newVal) => onSelect(newVal || INITIAL_STATE)}
          onInputChange={(_, newInputValue, reason) => {
            if (reason === "input") {
              setSearch(newInputValue);
              debouncedSearch(newInputValue);
            }
          }}
        />
      </Stack>
    </>
  );
};